import { Text } from '@chakra-ui/react';
import { LatestSplit, Member } from 'types/splits.types';
import { useAppSelector } from 'app/store';
import {
  getReleaseVersion,
  getVersionSyncStatus,
} from 'app/store/Releases/selectors';
import { useCallback, useMemo, useRef } from 'react';
import { sumMembersShares } from 'app/components/Song/Splits/utils';
import { useContributionRolesDDL, useResource } from 'app/utils/hooks';
import { fetchContributionClassesRolesDDLEffect } from 'app/store/SongList/effects';
import { getContributionClassNames } from 'app/store/SongList/selectors';
import { getCurrentProfileGuid } from 'app/store/Profile/selectors';
import { getColumns } from 'app/components/Song/Splits/LatestSplitList/utils';
import { Row } from '@tanstack/react-table';
import { LatestSplitRow } from 'app/components/Song/Splits';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Td, Tr } from 'app/components/Table';
import { useTotalWidth } from 'app/components/Core/StickyTable';

export const VersionSplitsTable = () => {
  const currentUserGuid = useAppSelector(getCurrentProfileGuid);
  const version = useAppSelector(getReleaseVersion);
  const syncState = useAppSelector((state) =>
    getVersionSyncStatus(state, version?.song.versionGuid || '')
  );

  useResource(
    fetchContributionClassesRolesDDLEffect,
    getContributionClassNames
  );
  const contributionRolesDDL = useContributionRolesDDL();

  const split = version?.split?.snapshot as LatestSplit;
  const members = Object.values(split?.members || {}).map((member) => ({
    ...member,
    share: Number(member.share ? +member.share / 100 : 0).toFixed(2),
  }));

  const sharesSum = useMemo(() => sumMembersShares(members || []), [members]);

  const ref = useRef<HTMLDivElement>(null);
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns({ totalWidth }), [totalWidth]);

  const rowRenderer = useCallback(
    (item: Row<Member>) => {
      return (
        <LatestSplitRow
          key={item.id}
          split={split}
          member={item.original}
          contributionRolesDDL={contributionRolesDDL}
          currentUserGuid={currentUserGuid}
        />
      );
    },
    [contributionRolesDDL, currentUserGuid, split]
  );

  return (
    <StickyTable<Member>
      tableKey="latest-splits"
      ref={ref}
      data={members}
      columns={columns}
      rowRenderer={rowRenderer}
      loading={syncState}
      footer={
        <Tr
          sx={{
            ' div': {
              bg: 'M.30',
            },
          }}
        >
          <Td />
          <Td />
          <Td
            sx={{ fontVariantNumeric: 'normal' }}
            data-testid="release-version-split-share-total"
          >
            <Text as="span" fontWeight={600}>
              Total:
            </Text>{' '}
            {sharesSum}%
          </Td>
          <Td />
          <Td />
          <Td />
        </Tr>
      }
    />
  );
};
